import { Editor, Extension, Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';
import React, { ReactNode } from 'react';

import { Icon } from '@eluve/components';

import { renderItems } from './renderItems';
import { Snippet } from './snippets';
import { getEditorExtensionOptions, removeEmptyListItems } from './utils';

export const SLASH_COMMAND_EXTENSION_NAME = 'slash-command';

export enum MenuGroup {
  STYLING = 'styling',
  SNIPPET = 'snippet',
}
export interface CommandItem {
  title: string;
  description: string;
  group: MenuGroup;
  searchTerms: string[];
  icon?: ReactNode;
  command: ({ editor, range }: { editor: Editor; range: Range }) => void;
  onItemSelect?: (item?: CommandItem) => void;
}

// Styling menu items
function getStylingItems(query: string): CommandItem[] {
  const styleItems: CommandItem[] = [
    {
      title: 'Text',
      description: 'Just start typing with plain text.',
      group: MenuGroup.STYLING,
      searchTerms: ['p', 'paragraph'],
      icon: <Icon name="Text" />,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleNode('paragraph', 'paragraph')
          .run();
      },
    },
    {
      title: 'Heading 1',
      description: 'Big section heading.',
      group: MenuGroup.STYLING,
      searchTerms: ['title', 'big', 'large'],
      icon: <Icon name="Heading1" />,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 1 })
          .run();
      },
    },
    {
      title: 'Heading 2',
      description: 'Medium section heading.',
      group: MenuGroup.STYLING,
      searchTerms: ['subtitle', 'medium'],
      icon: <Icon name="Heading2" />,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 2 })
          .run();
      },
    },
    {
      title: 'Heading 3',
      description: 'Small section heading.',
      group: MenuGroup.STYLING,
      searchTerms: ['subtitle', 'small'],
      icon: <Icon name="Heading3" />,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 3 })
          .run();
      },
    },
    {
      title: 'Bullet List',
      description: 'Create a simple bullet list.',
      group: MenuGroup.STYLING,
      searchTerms: ['unordered', 'point'],
      icon: <Icon name="List" />,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleBulletList().run();
      },
    },
    {
      title: 'Numbered List',
      description: 'Create a list with numbering.',
      group: MenuGroup.STYLING,
      searchTerms: ['ordered'],
      icon: <Icon name="ListOrdered" />,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleOrderedList().run();
      },
    },
    {
      title: 'Task List',
      description: 'Create a list with checkboxes.',
      group: MenuGroup.STYLING,
      searchTerms: ['todo', 'checkbox', 'check', 'task'],
      icon: <Icon name="SquareCheckBig" />,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleTaskList().run();
      },
    },
  ];
  // Optionally filter by query if provided:
  if (query.trim()) {
    const lower = query.toLowerCase();
    return styleItems.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) ||
        item.description.toLowerCase().includes(lower) ||
        item.searchTerms.some((term) => term.includes(lower)),
    );
  }
  return styleItems;
}

function getSnippetItems(editor: Editor, query: string): CommandItem[] {
  const extOptions = getEditorExtensionOptions(
    editor,
    SLASH_COMMAND_EXTENSION_NAME,
  );

  if (!extOptions || !extOptions.snippetOptions?.enabled) {
    return [];
  }
  const { snippets: snippetData } = extOptions.snippetOptions;

  if (!snippetData.length) {
    return [];
  }

  let filtered = snippetData;
  if (query.trim()) {
    const lower = query.toLowerCase();
    filtered = snippetData.filter(
      (item: Snippet) =>
        item?.title.toLowerCase().includes(lower) ||
        item?.content.toLowerCase().includes(lower),
    );
  }
  return filtered.map((snippet: Snippet) => ({
    title: snippet.title,
    description: snippet.content ?? '',
    group: MenuGroup.SNIPPET,
    searchTerms: [snippet.title.toLowerCase()],
    command: ({ editor, range }: { editor: Editor; range: Range }) => {
      const cleanedContent = removeEmptyListItems(snippet?.content).trim();

      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent(cleanedContent)
        .run();
    },
    onItemSelect: () => {
      extOptions.snippetOptions?.onItemSelect?.(snippet);
    },
  }));
}

// Merge base items and snippet items, limiting snippet display
function getSuggestionItems({
  editor,
  query,
}: {
  query: string;
  editor: Editor;
}): CommandItem[] {
  const stylingItems = getStylingItems(query);
  const snippetItems = getSnippetItems(editor, query);
  return [...stylingItems, ...snippetItems];
}

const slashCommandPluginKey = new PluginKey(SLASH_COMMAND_EXTENSION_NAME);

export const SlashCommandExtension = Extension.create({
  name: SLASH_COMMAND_EXTENSION_NAME,
  addOptions() {
    return {
      suggestion: {
        char: '/',
        allowSpaces: true,
        command: ({
          editor,
          range,
          props,
        }: {
          editor: Editor;
          range: Range;
          props: CommandItem;
        }) => {
          props.command({ editor, range });
          props.onItemSelect?.();
        },
        items: getSuggestionItems,
        render: renderItems,
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        pluginKey: slashCommandPluginKey,
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
